import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, Check, FileText, ShieldAlert, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useLang } from '@/lib/i18n';

const ICONS = {
  quote_received: FileText,
  quote_accepted: Check,
  quote_rejected: FileText,
  payment_escrow: Wallet,
  work_completed: Check,
  payment_released: Wallet,
  dispute_opened: ShieldAlert,
};

const COLORS = {
  quote_received: 'bg-[#EAF3FF] text-[#1677F2]',
  quote_accepted: 'bg-[#DFF7EA] text-[#16764A]',
  quote_rejected: 'bg-slate-100 text-slate-500',
  payment_escrow: 'bg-[#FFF6C7] text-[#7A5F00]',
  work_completed: 'bg-[#EAF3FF] text-[#1677F2]',
  payment_released: 'bg-[#DFF7EA] text-[#16764A]',
  dispute_opened: 'bg-[#FFE1E4] text-[#A10B18]',
};

export default function NotificationsPage() {
  const { t } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get('/notifications/mine').then((r) => {
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    await api.post('/notifications/mark-read', {});
    load();
    window.dispatchEvent(new Event('jobkongo:notifs-read'));
  };

  const open = async (n) => {
    if (!n.read) {
      await api.post('/notifications/mark-read', { notification_id: n.id });
      window.dispatchEvent(new Event('jobkongo:notifs-read'));
    }
    if (n.link) nav(n.link);
    else load();
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 pb-24">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0B1220] flex items-center gap-2" data-testid="notifications-title">
          <BellRing size={24} className="text-[#1677F2]" /> {t('notif.title')}
          {unread > 0 && <Badge className="bg-[#CE1021] text-white" data-testid="notifications-unread-badge">{unread}</Badge>}
        </h1>
        {unread > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead} data-testid="notifications-mark-all-btn">
            {t('notif.markAll')}
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-6">{t('notif.subtitle')}</p>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed" data-testid="notifications-empty-state">
          <Bell size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">{t('notif.empty')}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            const color = COLORS[n.type] || 'bg-slate-100 text-slate-500';
            return (
              <button
                key={n.id}
                onClick={() => open(n)}
                className={`w-full text-left rounded-xl border p-4 flex items-start gap-3 transition-colors duration-150 hover:border-[#9DCEFF] ${n.read ? 'bg-white border-[#E2E8F0]' : 'bg-[#F2F8FF] border-[#C7E2FF]'}`}
                data-testid={`notification-item-${n.id}`}
              >
                <span className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm truncate ${n.read ? 'font-medium text-slate-700' : 'font-bold text-[#0B1220]'}`}>{n.title}</p>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-[#1677F2] flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">{t('notif.smsTag')}</span>
                    <span className="text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
